import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbActiveModal, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { NgxSpinnerService } from "ngx-spinner";
import { Subscription } from "rxjs";

import { Guid } from "guid-typescript";

// models
import { ErrorModel } from "@shared/models/ErrorModel";
import { PaymentResponse, CuponPagoTarjeta, Tarjeta, PagoElectronicoDto } from "@shared/models/facturacion";
import { TipoPago } from "@shared/enums/tipoPago";
import { PaymentState } from "@shared/enums/PaymentState";
import { PaymentStatus } from "@shared/enums/PaymentStatus";

// services 
import { ComprobanteService } from "@facturacion/comprobante.service";
import { SharedService } from "@shared/shared.service";
import { MessageService } from "@shell/message.service";

// common
import { ModalConfirmComponent } from "@shared/generic-modals/modal-confirm.component";
import { UsuarioService } from "@account/usuario.service";
import { environment } from "@environments";
import { HttpErrorResponse } from "@angular/common/http";
import { Aeroplanta } from "@shared/models/despacho";
import { isEmptyValue } from "@shared/compat-tools";

@Component({
  selector: 'app-payment-manager',
  templateUrl: './payment-manager.component.html',
  styleUrls: ['./payment-manager.component.scss']
})
export class PaymentManagerComponent implements OnInit, OnDestroy {

  @Input() listaCupones: CuponPagoTarjeta[];
  @Input() importe: number;
  @Input() paymentType: TipoPago;
  @Output() nuevoCupon: EventEmitter<CuponPagoTarjeta> =
    new EventEmitter<CuponPagoTarjeta>();
  @Output() payment: EventEmitter<PagoElectronicoDto> =
    new EventEmitter<PagoElectronicoDto>();

  @Input() listaAerovales: string[] = [];
  @Input() points: [] = [];

  cargandoTarjetas: boolean = false;
  cargandoAeroplanta: boolean = false;
  errorProductos: boolean;
  tarjetas: Tarjeta[];
  file: File;
  error: string = null;
  public titulo: string;
  TipoPago = TipoPago;

  pagosForm: FormGroup;
  cargandoPoints: boolean = false;
  blockExit: boolean = false;
  processMessage: string;
  facturaId: string;

  currentState: PaymentState = PaymentState.Indeterminado;
  currentStateSubscription: Subscription;
  PaymentState = PaymentState;

  interval: any;
  timeLeft: number = 60;
  Payment: PaymentStatus;
  preferenceId: string;

  aeroplanta: Aeroplanta;

  constructor(
    public activeModal: NgbActiveModal,
    private comprobanteService: ComprobanteService,
    private sharedService: SharedService,
    private formBuilder: FormBuilder,
    private messageService: MessageService,
    private spinner: NgxSpinnerService,
    private modalService: NgbModal,
    private usuarioService: UsuarioService
  ) {
    this.currentStateSubscription = this.sharedService.currentState$.subscribe(
      (state) => {
        this.currentState = state;
        this.changeSpinner(state);
      }
    );
  }

  ngOnInit() {    
    this.processMessage = '';
    this.currentState = PaymentState.Indeterminado;
    this.initUi(this.paymentType);
    this.initForm(this.paymentType);
  }

  ngOnDestroy() {
    this.currentStateSubscription.unsubscribe();
    this.clearTimer();
  }

  initForm(tipoPago: TipoPago) {
    this.pagosForm = this.formBuilder.group({
      tarjetaFormControl: ['', tipoPago === TipoPago.Indeterminado ? Validators.required : null],
      numeroTarjetaFormControl: ['', tipoPago === TipoPago.Indeterminado ? Validators.required : null],
      loteCuponFormControl: ['', tipoPago === TipoPago.Indeterminado ? Validators.required : null],
      numeroCuponFormControl: ['', tipoPago === TipoPago.Indeterminado ? Validators.required : null],
      importeFormControl: [this.importe ? this.importe : '', [Validators.required, Validators.max(this.importe)]],
      pointFormControl: ['', tipoPago == TipoPago.Point ? Validators.required : null],
    });
  }

  initUi(tipoPago: TipoPago) {
    switch (tipoPago) {
      case TipoPago.MercadoPago:
        this.titulo = "Método de Pago: MERCADO PAGO";
        break;
      case TipoPago.AppYpf:
        this.titulo = "Método de Pago: APP YPF";
        break;
      case TipoPago.Point:
        this.titulo = "Método de Pago: POINT SMART";
        break;
      default:
        this.titulo = "Nuevo cupon tarjeta de Crédito/Débito";
        this.getTarjetas();
        break;
    }
    this.obtenerAeroplanta();
  }

  getTarjetas() {
    this.cargandoTarjetas = true;
    this.comprobanteService.getTarjetas().subscribe(
      (result) => {
        this.tarjetas = result;
        this.cargandoTarjetas = false;
      },
      () => {
        this.cargandoTarjetas = false;
      }
    );
  }

  obtenerAeroplanta() {
    this.cargandoAeroplanta = true;
    this.comprobanteService.getDatosAeroplanta(this.usuarioService.getAeroplantaActual().codigoAeroplanta)
      .subscribe(result => {
        this.aeroplanta = result[0];
        this.cargandoAeroplanta = false;
      }, () => {
        const msg = 'Hubo un error al obtener los datos de la aeroplanta.';
        this.messageService.showErrorMessage(msg);
        this.error = msg;
      });
  }

  get validCupones() {
    this.error = null;

    if (!this.file) {
      this.error = "Debe seleccionar un archivo adjunto del cupón.";
    }

    if (
      this.estaNumeroCuponEnTarjeta(
        this.loteCuponFormControl.value,
        this.numeroCuponFormControl.value,
        this.numeroTarjetaFormControl.value
      )
    ) {
      this.error = "Este número de cupón ya ha sido cargado.";
    }

    return this.error == null;
  }

  controlarImporte() {
    if (this.importe < this.importeFormControl.value) {
      return false;
    }
    return true;
  }

  onClick() {
    switch (this.paymentType) {
      case TipoPago.Indeterminado:
        this.procesarPagoConCupones();
        break;

      default:
        this.procesarPagosElectronicos();
        break;
    }
  }

  onClose() {
    if (this.currentState === PaymentState.IntencionPagoSolicitado || this.currentState === PaymentState.IntencionPagoConfirmado) {
      const modalRef = this.modalService.open(ModalConfirmComponent, {centered: true});
      modalRef.componentInstance.mensaje = 'Antes de cancelar la operación, es importante verificar que el pago aún no se haya efectuado. ¿Desea continuar con la cancelación de la intención de pago?';
      modalRef.result.then(() => {
        // this.eliminarPagoElectronico();
        this.activeModal.close(false);
      });
    } else {
      this.activeModal.close(false);
    }
  }

  procesarPagoConCupones() {
    if (this.pagosForm.invalid) {
      this.messageService.showErrorMessage('Debe revisar los campos no son válidos.');
      return;
    }

    if (this.validCupones) {
      const c = new CuponPagoTarjeta();
      c.tarjeta = this.tarjetaFormControl.value;
      c.numeroTarjeta = this.numeroTarjetaFormControl.value;
      c.numeroLote = this.loteCuponFormControl.value;
      c.numeroCupon = this.numeroCuponFormControl.value;
      c.importe = this.importeFormControl.value;
      c.pathCuponFisico = this.file.name;
      c._file = this.file;
      this.nuevoCupon.emit(c);
      this.activeModal.close(true);
    }
  }

  procesarPagosElectronicos() {
    if (this.pagosForm.invalid) {
      this.messageService.showErrorMessage('Debe revisar los campos no son válidos.');
      return;
    }

    if (isEmptyValue(this.aeroplanta) || isEmptyValue(this.aeroplanta.identificadorFisicoCara)) {
      const msg = 'No existe un identificador interno (POSID) configurado para la aeroplanta: '+ this.usuarioService.getAeroplantaActual().nombre+'.';
      this.messageService.showErrorMessage(msg);
      this.error = msg
      return;
    }

    this.changeState(PaymentState.IntencionPagoSolicitado);
    const ventaDto = new PagoElectronicoDto();
    ventaDto.preferenceId = Guid.create().toString();
    ventaDto.articuloDescripcion = 'VENTA PAD YPF';
    ventaDto.cantidad = 1;
    (this.paymentType === TipoPago.Point) ? ventaDto.deviceId = this.pointFormControl.value.id : null;
    const fecha = new Date();
    ventaDto.fecha = this.formatDate(fecha.getFullYear(), fecha.getMonth() + 1, fecha.getDate(), fecha.getHours(), fecha.getMinutes());
    ventaDto.importe = Number(this.importeFormControl.value);
    if (this.inProduction) {
      ventaDto.identificadorFisicoCara = this.aeroplanta.identificadorFisicoCara ?? 0;
    } else {
      ventaDto.identificadorFisicoCara = (this.paymentType === TipoPago.AppYpf) ? 1215 : (this.aeroplanta.identificadorFisicoCara ?? 1);
    }
    ventaDto.tipoPago = this.paymentType;
    ventaDto.facturaId = this.facturaId;
    if (this.inProduction) {
      ventaDto.legacySiteId = this.usuarioService.getAeroplantaActual().legacySiteId ?? 0;
    } else {
      ventaDto.legacySiteId = (this.paymentType === TipoPago.MercadoPago) ? 103081 : 670049;
    }
    console.log('venta DTO: ', JSON.stringify(ventaDto));
    this.comprobanteService.postPublicarPagoElectronico(ventaDto)
      .subscribe(
        (r: PaymentResponse) => {
          if (r.ejecucionExitosa) {
            this.preferenceId = ventaDto.preferenceId;
            this.changeState(PaymentState.IntencionPagoConfirmado);
            this.obtenerEstadoDePagoElectronico();
          } else {
            this.messageService.showErrorMessage(r.eventualMensajeError);
            this.changeState(PaymentState.IntencionPagoConError);
          }
        },
        (error: any) => {
          if (error instanceof HttpErrorResponse) {
            if (error.error) {
              this.messageService.showErrorMessage('Error al crear el pago electronico.\n' + error.error.eventualMensajeError);
            }
          } else if (error instanceof ErrorModel) {
            this.messageService.showErrorMessage(error.message + "\n" + error.description);
          } else {
            this.messageService.showErrorMessage('Error al crear el pago electrónico.\n ' + JSON.stringify(error));
          }
          setTimeout(() => {
            this.changeState(PaymentState.IntencionPagoConError);
          }, 4000);
        }
      );
  }

  obtenerEstadoDePagoElectronico() {
    this.interval = setInterval(() => {
      if (this.timeLeft > 0) {
        this.timeLeft--;
        
        this.comprobanteService.postObtenerEstadoDePagoElectronico(this.listaAerovales)
          .subscribe((pago:PagoElectronicoDto[]) => {
            if (pago.length > 0) {
              const p = pago.find(p => p.preferenceId === this.preferenceId && p.estado !== PaymentStatus.in_process);
              if (p != null) {
                p._alias = this.pointFormControl.value.alias;
                p._numero = this.pointFormControl.value.nroPoint;
                this.payment.emit(p);
                console.log('pago: ', p);
                this.activeModal.close(p.estado == PaymentStatus.approved ? true : false);
              }
            }
          }, ()=>{
            this.clearTimer();
          });

      } else {
        this.clearTimer();
      }
    }, 5000);    
  }

  async eliminarPagoElectronico() {
    try {
      const r = await this.comprobanteService.deleteEliminarPagoElectronico(this.facturaId);
      if (r) {
        this.messageService.showSuccessMessage('El pago fue desestimado correctamente.');
        this.activeModal.close(false);
      } else {
        this.messageService.showSuccessMessage('El pago no fue desestimado correctamente.');
      }      
    } catch (err) {
      this.messageService.showSuccessMessage('Hubo un error al intentar desestimar el pago.\n' + err.message);
    }    
  }

  clearTimer(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  get fileName(): string {
    if (this.file) {
      return this.file.name;
    }
    return "";
  }

  onSelectedFile(event) {
    const selectedFile: File = event.target.files[0];
    if (selectedFile) {
      const date = new Date();
      const now =
        date.getFullYear() +
        ("0" + (date.getMonth() + 1)).slice(-2) +
        ("0" + date.getDate()).slice(-2) +
        ("0" + date.getHours()).slice(-2) +
        ("0" + date.getMinutes()).slice(-2) +
        ("0" + date.getSeconds()).slice(-2);
      const newName: string = `JPEG_CUPON_${now}.${
        selectedFile.name.split(".")[1]
      }`; // Cambia esto al nuevo nombre que desees
      const modifiedFile = new File([selectedFile], newName, {
        type: selectedFile.type,
      });

      this.sharedService.resizeImage(modifiedFile, 1500).then((blob) => {
        this.file = new File([blob], modifiedFile.name, { type: "image/jpeg" });
      });
    }
  }

  // validaciones

  estaNumeroCuponEnTarjeta(numeroLote: number, numeroCupon: number, numeroTarjeta: number): boolean
  {
    const encontre= this.listaCupones.some(element=> element.numeroCupon==numeroCupon && element.numeroLote==numeroLote && element.numeroTarjeta==numeroTarjeta)
    return encontre;
  }

  // utils
  formatDate(year, month, day, hour, minute): string {
    return year + '-' + month.toLocaleString(undefined, { minimumIntegerDigits: 2 }) + '-' + day.toLocaleString(undefined, { minimumIntegerDigits: 2 })
      + 'T' + hour.toLocaleString(undefined, { minimumIntegerDigits: 2 }) + ':' + minute.toLocaleString(undefined, { minimumIntegerDigits: 2 }) + ':00';
  }

  changeSpinner(state?: PaymentState) {
    console.log(state);
    switch (state) {
      case PaymentState.IntencionPagoSolicitado:
        this.spinner.show('spinnerImporteFactura');
        this.processMessage = 'Procesando pago...';
        break;
      case PaymentState.IntencionPagoConfirmado:
        this.spinner.show('spinnerImporteFactura');
        this.processMessage = 'Esperando confimación de pago...';
        break;
      default:
        this.spinner.hide('spinnerImporteFactura');
        this.processMessage = '';
        break;
    }
  }

  changeState(newState: PaymentState) {
    this.sharedService.currentState = newState;
  }

  get tarjetaFormControl() {
    return this.pagosForm.controls.tarjetaFormControl;
  }
  get numeroTarjetaFormControl() {
    return this.pagosForm.controls.numeroTarjetaFormControl;
  }
  get loteCuponFormControl() {
    return this.pagosForm.controls.loteCuponFormControl;
  }
  get numeroCuponFormControl() {
    return this.pagosForm.controls.numeroCuponFormControl;
  }
  get importeFormControl() {
    return this.pagosForm.controls.importeFormControl;
  }
  get pointFormControl() {
    return this.pagosForm.controls.pointFormControl;
  }
  get showProccess() {
    return this.processMessage != "";
  }

  get inProduction(): boolean {
    return environment.name === 'production';
  }
}
